import React, { createContext, useReducer, useMemo, useCallback, useContext, useState, useRef, useEffect } from 'react'
import { useStatusOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  initialMemoryBookState,
  memoryBooksReducer,
  ADD_PAGE,
  DELETE_PAGE,
  TURN_PAGE,
  UPDATE_PAGE_ORDER,
  UPDATE_VIEWING_PAGES,
  UPDATE_PAGE_LAYOUT,
  UPDATE_PAGE_CONTENT,
  UPDATE_PAGE_STICKERS,
  UPDATE_PAGE_CUSTOM_LAYOUT_ELEMENTS,
  UPDATE_PAGE_BACKGROUND,
  SET_MOBILE_MODE,
  SET_BOOK,
  RESTORE_FROM_SNAPSHOT,
} from './MemoryBooksReducer'
import {
  deletePageAPI,
  postBlankBookPages,
  useGetFirstBook,
  updatePageOrderAPI,
  updatePageLayoutAPI,
  updateMemoryBook,
  getBookById,
} from '../services/MemoryBooksAPI/memoryBooksAPI'
import TemplateBlueprints from '../components/PageAssembler/TemplateBlueprints'
import { getMemory } from '../services/MemoriesAPI/memoriesAPI'
import { useUndoRedo } from '../hooks/useUndoRedo/useUndoRedo'

const MemoryBookContext = createContext()

const normalizePageId = (value) => {
  if (value === 'front' || value === 'back') {
    return value
  }

  if (typeof value === 'number') {
    return value
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

const buildPagePayload = (page) => ({
  templateId: page.templateId,
  elements: page.elements,
  stickers: page.stickers,
  customLayoutElements: page.customLayoutElements,
  backgroundId: page.backgroundId,
})

export function useMemoryBook() {
  const context = useContext(MemoryBookContext)

  if (!context) {
    throw new Error('useMemoryBook must be used within a MemoryBookProvider')
  }
  return context
}

export function MemoryBookProvider({ children, siteConfig, isAuthenticated = false, skipFirstBookFetch = false }) {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const [memoryBook, dispatch] = useReducer(memoryBooksReducer, undefined, initialMemoryBookState)
  const { bookId: initialBookId, error, loading } = useGetFirstBook(dispatch, isAuthenticated, skipFirstBookFetch)

  // Whether loadBook has finished (resolved or failed). Only meaningful while
  // skipFirstBookFetch is set, which the isLoading derivation below reads at render time.
  const [bookLoadSettled, setBookLoadSettled] = useState(false)

  // Check if undo/redo feature is enabled
  const isUndoRedoEnabled = useFeatureFlag('ginkgo_memoryBooks_undoRedo').isOn

  // Use the bookId from memoryBook state (updated when loadBook is called) or fall back to initialBookId
  const bookId = memoryBook?.id || initialBookId

  // Keep a ref to the latest memoryBook state for snapshot capture
  const memoryBookRef = useRef(memoryBook)
  useEffect(() => {
    memoryBookRef.current = memoryBook
  }, [memoryBook])

  // Track book loading state to prevent duplicate concurrent requests
  const loadingRef = useRef(false)
  const loadedBookIdRef = useRef(null)

  // Reset loaded book ID when switching to a different book
  useEffect(() => {
    if (bookId && loadedBookIdRef.current && loadedBookIdRef.current !== bookId) {
      loadedBookIdRef.current = null
    }
  }, [bookId])

  // Track saving state for auto-save operations
  const [isSaving, setIsSaving] = useState(false)

  // Auto-save: persists current state to backend after undo/redo operations
  // Uses single PUT /books/{bookId} endpoint to update entire book atomically
  const autoSave = useCallback(
    async (snapshot) => {
      if (!isUndoRedoEnabled || !bookId) return

      setIsSaving(true)

      try {
        // Use snapshot data if provided (from undo/redo),
        // otherwise fall back to current state (shouldn't happen in practice)
        await updateMemoryBook({
          bookId,
          title: snapshot?.title || memoryBook.title,
          description: snapshot?.description || memoryBook.description,
          contentPages: snapshot?.contentPages || memoryBook.contentPages,
        })

        // Silent success - no overlay needed for auto-save
      } catch (saveError) {
        console.error('Error auto-saving after undo/redo:', saveError)
        showStatusOverlay({
          message: t('controlButtons.autoSaveFailed', 'Failed to save changes. Please try again.'),
          type: 'error',
          dismissable: true,
        })
      } finally {
        setIsSaving(false)
      }
    },
    [isUndoRedoEnabled, bookId, memoryBook.title, memoryBook.description, memoryBook.contentPages, showStatusOverlay, t]
  )

  // Undo/Redo handlers - these are called by useUndoRedo when undo/redo is invoked
  // Auto-saves after restoring state to keep backend in sync
  const handleUndo = useCallback(
    async (previousSnapshot) => {
      dispatch({
        type: RESTORE_FROM_SNAPSHOT,
        payload: previousSnapshot,
      })

      // Auto-save restored state to backend
      // Pass previousSnapshot directly to avoid reading stale state
      await autoSave(previousSnapshot)
    },
    [autoSave]
  )

  const handleRedo = useCallback(
    async (nextSnapshot) => {
      dispatch({
        type: RESTORE_FROM_SNAPSHOT,
        payload: nextSnapshot,
      })

      // Auto-save restored state to backend
      // Pass nextSnapshot directly to avoid reading stale state
      await autoSave(nextSnapshot)
    },
    [autoSave]
  )

  // Initialize undo/redo functionality only if feature flag is enabled
  const undoRedoControls = useUndoRedo(
    isUndoRedoEnabled ? bookId : null,
    isUndoRedoEnabled ? handleUndo : null,
    isUndoRedoEnabled ? handleRedo : null
  )

  // Helper to create snapshot and push to history
  const pushHistorySnapshot = useCallback(
    (actionType) => {
      if (!bookId || !isUndoRedoEnabled) return

      // Use ref to capture the latest state at the time of API success
      const currentBook = memoryBookRef.current

      const snapshot = {
        contentPages: currentBook.contentPages,
        currentPages: currentBook.currentPages,
        id: currentBook.id,
        createdDate: currentBook.createdDate,
        title: currentBook.title,
        description: currentBook.description,
      }

      undoRedoControls.pushHistory(snapshot, actionType)
    },
    [bookId, isUndoRedoEnabled, undoRedoControls]
  )

  // Perform undo
  const performUndo = useCallback(() => {
    if (!isUndoRedoEnabled || isSaving) return null

    const currentState = {
      contentPages: memoryBook.contentPages,
      currentPages: memoryBook.currentPages,
      id: memoryBook.id,
      createdDate: memoryBook.createdDate,
      title: memoryBook.title,
      description: memoryBook.description,
    }
    return undoRedoControls.undo(currentState)
  }, [isUndoRedoEnabled, isSaving, undoRedoControls, memoryBook])

  // Perform redo
  const performRedo = useCallback(() => {
    if (!isUndoRedoEnabled || isSaving) return null

    const currentState = {
      contentPages: memoryBook.contentPages,
      currentPages: memoryBook.currentPages,
      id: memoryBook.id,
      createdDate: memoryBook.createdDate,
      title: memoryBook.title,
      description: memoryBook.description,
    }
    return undoRedoControls.redo(currentState)
  }, [isUndoRedoEnabled, isSaving, undoRedoControls, memoryBook])

  // Add a page to the book. You can specify how many pages to add as well as where to add them.
  const addBookPages = useCallback(
    async (numOfPagesToAdd = 1, addAfterThisPageNum = memoryBook.contentPages.length - 2, isMobile = false) => {
      if (!bookId) {
        console.error('Book ID is not set. Could not add pages.')
        return
      }
      // If The user chooses nothing, just return out.
      const pagesToAdd = Number(numOfPagesToAdd) || 0

      if (pagesToAdd === 0) {
        return
      }

      const pageNum = Number(addAfterThisPageNum)

      const insertPageNum =
        pageNum >= 0 && pageNum < memoryBook.contentPages.length - 2 ? pageNum : memoryBook.contentPages.length - 2

      // Capture state BEFORE adding pages
      pushHistorySnapshot(ADD_PAGE)

      const onSuccess = (response) => {
        showStatusOverlay({
          message: t('MemoryBookProvider.successAddingPages.description', 'Added {pagesToAdd} page(s) to the book.', {
            pagesToAdd,
          }),
          type: 'success',
          dismissable: true,
        })
        dispatch({
          type: ADD_PAGE,
          payload: {
            numOfPagesToAdd: pagesToAdd,
            addAfterThisPageNum: insertPageNum,
            isMobile,
            response: response.data,
          },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemAddingPages.description',
            'There was a problem adding the pages, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await postBlankBookPages({
        bookId,
        startingPageNumber: pageNum,
        pageCount: pagesToAdd,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages.length, showStatusOverlay, t, pushHistorySnapshot]
  )

  const deleteBookPage = useCallback(
    async (pageNumber, isMobile = false) => {
      // Capture state BEFORE deleting page
      pushHistorySnapshot(DELETE_PAGE)

      const onSuccess = (response) => {
        showStatusOverlay({
          message: t('MemoryBookProvider.successDeletingPage.description', 'Successfully Deleted Page {pageNumber}', {
            pageNumber,
          }),
          type: 'success',
          dismissable: true,
        })
        dispatch({ type: DELETE_PAGE, payload: { pageNumber, isMobile, response: response.data } })
      }
      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemDeletingPage.description',
            'There was a problem deleting the page, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }
      deletePageAPI({
        bookId,
        pageNumber,
        onSuccess,
        onError,
      })
    },
    [bookId, showStatusOverlay, t, pushHistorySnapshot]
  )

  const turnBookPage = useCallback(
    (direction, isMobile = false) => {
      dispatch({ type: TURN_PAGE, payload: { direction, isMobile } })
    },
    [dispatch]
  )

  const updatePageOrder = useCallback(
    (dropPageNumber, draggedIndexPosition) => {
      const draggedPageId = memoryBook.contentPages[draggedIndexPosition].id

      // Capture state BEFORE reordering pages
      pushHistorySnapshot(UPDATE_PAGE_ORDER)

      updatePageOrderAPI({
        bookId,
        page: draggedPageId,
        newPageNumber: dropPageNumber,
        onSuccess: (response) => {
          dispatch({ type: UPDATE_PAGE_ORDER, payload: { response: response.data } })
        },
      })
    },
    [memoryBook.contentPages, bookId, dispatch, pushHistorySnapshot]
  )

  const updateViewingPages = useCallback(
    ({ pageNumber, isMobile = false }) => {
      dispatch({ type: UPDATE_VIEWING_PAGES, payload: { pageNumber, isMobile } })
    },
    [dispatch]
  )

  const updatePageLayout = useCallback(
    async (pageNumber, newTemplateId, props) => {
      // Capture state BEFORE making the change
      pushHistorySnapshot(UPDATE_PAGE_LAYOUT)

      const elements = TemplateBlueprints(newTemplateId, props)
      const onSuccess = (response) => {
        dispatch({ type: UPDATE_PAGE_LAYOUT, payload: { pageNumber, response: response.data } })
      }
      updatePageLayoutAPI({
        bookId,
        templateId: newTemplateId,
        page: pageNumber,
        elements,
        onSuccess,
      })
    },
    [bookId, pushHistorySnapshot]
  )

  const updatePageContent = useCallback(
    async (pageNumber, positionIndex, updates = {}, retryCount = 0) => {
      const MAX_RETRIES = 5
      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            // Retry the function after a delay
            await updatePageContent(pageNumber, positionIndex, updates, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }
      const selectedElement = selectedPage.elements[positionIndex]

      // Get current props without mutating (safety check for legacy data)
      const currentProps = selectedElement.props || {}

      // Only check properties that are being updated
      const updateNeeded = Object.keys(updates).some((key) => {
        if (key === 'memoryId') {
          return currentProps.memoryId !== updates.memoryId
        }
        if (key === 'caption') {
          return currentProps.caption !== updates.caption
        }
        if (key === 'title') {
          return currentProps.title !== updates.title
        }
        if (key === 'cropArea') {
          const currentCropArea = currentProps.cropArea || undefined
          const newCropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          return currentCropArea !== newCropArea
        }
        return false
      })

      // Capture state BEFORE making the change (BEFORE any mutations)
      if (updateNeeded) {
        pushHistorySnapshot(UPDATE_PAGE_CONTENT)
      }

      const updatePageElements = (passedPage) => {
        // Create a new elements array to avoid mutation
        const updatedElements = passedPage.elements.map((element, idx) => {
          if (idx !== positionIndex) {
            return element
          }

          const elementProps = element.props || {}

          if (element.type === 'text') {
            return {
              ...element,
              props: {
                ...elementProps,
                ...(updates.memoryId !== undefined && { memoryId: updates.memoryId }),
              },
            }
          }

          return {
            ...element,
            props: {
              ...elementProps,
              ...(updates.memoryId !== undefined && { memoryId: updates.memoryId }),
              ...(updates.caption !== undefined && { caption: updates.caption }),
              ...(updates.title !== undefined && { title: updates.title }),
              ...(updates.cropArea !== undefined && {
                cropArea: updates.cropArea ? JSON.stringify(updates.cropArea) : undefined,
              }),
            },
          }
        })

        return updatedElements
      }

      // Optimistically update UI immediately for responsive feel
      if (updateNeeded) {
        if (selectedElement.type === 'text') {
          const payload = {
            pageNumber,
            positionIndex,
          }
          if (updates.memoryId !== undefined) {
            payload.updatedId = updates.memoryId
          }
          dispatch({
            type: UPDATE_PAGE_CONTENT,
            payload,
          })
        } else if (selectedElement.type === 'image' || selectedElement.type === 'backPage') {
          const payload = {
            pageNumber: normalizedPageNumber,
            positionIndex,
          }
          if (updates.memoryId !== undefined) {
            payload.updatedId = updates.memoryId
          }
          if (updates.caption !== undefined) {
            payload.caption = updates.caption
          }
          if (updates.title !== undefined) {
            payload.title = updates.title
          }
          if (updates.cropArea !== undefined) {
            payload.cropArea = updates.cropArea
          }
          dispatch({
            type: UPDATE_PAGE_CONTENT,
            payload,
          })
        }
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingPages.description',
            'There was a problem updating the page, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      if (updateNeeded) {
        await updatePageLayoutAPI({
          bookId,
          page: normalizedPageNumber,
          ...buildPagePayload(selectedPage),
          elements: updatePageElements(selectedPage),
          onError,
        })
      }
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t, pushHistorySnapshot]
  )

  const updatePageStickers = useCallback(
    async (pageNumber, stickers = [], retryCount = 0) => {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await updatePageStickers(pageNumber, stickers, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }

      const sanitizedStickers = Array.isArray(stickers)
        ? stickers
            .filter((sticker) => sticker?.id)
            .map((sticker) => ({
              ...sticker,
              dimensions: { ...sticker.dimensions },
            }))
        : []

      const existingStickers = Array.isArray(selectedPage.stickers) ? selectedPage.stickers : []

      const existingSerialized = JSON.stringify(existingStickers)
      const nextSerialized = JSON.stringify(sanitizedStickers)

      if (existingSerialized === nextSerialized) {
        return
      }

      // Capture state BEFORE making the change
      pushHistorySnapshot(UPDATE_PAGE_STICKERS)

      // Optimistically update UI immediately for responsive feel
      dispatch({
        type: UPDATE_PAGE_STICKERS,
        payload: { pageNumber: normalizedPageNumber, stickerState: sanitizedStickers },
      })

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingStickers.description',
            'There was a problem updating stickers, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        stickers: sanitizedStickers,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t, pushHistorySnapshot]
  )

  const updatePageCustomLayoutElements = useCallback(
    async (pageNumber, customLayoutElements = [], retryCount = 0) => {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await updatePageCustomLayoutElements(pageNumber, customLayoutElements, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }

      const sanitizedElements = Array.isArray(customLayoutElements)
        ? customLayoutElements
            .filter((element) => element?.id)
            .map((element) => ({
              ...element,
              dimensions: element.dimensions ? { ...element.dimensions } : undefined,
            }))
        : []

      const existingElements = Array.isArray(selectedPage.customLayoutElements) ? selectedPage.customLayoutElements : []

      const existingSerialized = JSON.stringify(existingElements)
      const nextSerialized = JSON.stringify(sanitizedElements)

      if (existingSerialized === nextSerialized) {
        return
      }

      // Capture state BEFORE making the change
      pushHistorySnapshot(UPDATE_PAGE_CUSTOM_LAYOUT_ELEMENTS)

      const onSuccess = () => {
        dispatch({
          type: UPDATE_PAGE_CUSTOM_LAYOUT_ELEMENTS,
          payload: { pageNumber: normalizedPageNumber, customLayoutElements: sanitizedElements },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingCustomLayoutElements.description',
            'There was a problem updating layout elements, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        customLayoutElements: sanitizedElements,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t, pushHistorySnapshot]
  )

  const updateCustomLayoutElementData = useCallback(
    async (pageNumber, elementId, updates = {}) => {
      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)

      if (!selectedPage) {
        return
      }

      const existingElements = Array.isArray(selectedPage.customLayoutElements) ? selectedPage.customLayoutElements : []
      const element = existingElements.find((el) => el?.id === elementId)

      if (!element) {
        return
      }

      // Check if update is needed
      const currentPickerData = element.pickerData || {}
      const updateNeeded = Object.keys(updates).some((key) => {
        if (key === 'cropArea') {
          const currentCropArea = currentPickerData.cropArea || undefined
          const newCropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          return currentCropArea !== newCropArea
        }
        return currentPickerData[key] !== updates[key]
      })

      if (!updateNeeded) {
        return
      }

      // Create updated elements array
      const updatedElements = existingElements.map((el) => {
        if (el.id !== elementId) {
          return el
        }

        const updatedPickerData = { ...currentPickerData }

        Object.keys(updates).forEach((key) => {
          if (key === 'cropArea') {
            updatedPickerData.cropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          } else {
            updatedPickerData[key] = updates[key]
          }
        })

        return {
          ...el,
          pickerData: updatedPickerData,
        }
      })

      await updatePageCustomLayoutElements(pageNumber, updatedElements)
    },
    [memoryBook.contentPages, updatePageCustomLayoutElements]
  )

  const updatePageBackground = useCallback(
    async function handleUpdatePageBackground(pageNumber, backgroundId, retryCount = 0) {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await handleUpdatePageBackground(pageNumber, backgroundId, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)

      if (!selectedPage) {
        return
      }

      const nextBackgroundId = backgroundId ?? null
      const currentBackgroundId = selectedPage.backgroundId ?? null

      if (currentBackgroundId === nextBackgroundId) {
        return
      }

      // Capture state BEFORE making the change
      pushHistorySnapshot(UPDATE_PAGE_BACKGROUND)

      const onSuccess = () => {
        dispatch({
          type: UPDATE_PAGE_BACKGROUND,
          payload: { pageNumber: normalizedPageNumber, backgroundId: nextBackgroundId },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingBackground.description',
            'There was a problem updating the background, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        backgroundId: nextBackgroundId,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t, pushHistorySnapshot]
  )

  // Batch update multiple page backgrounds atomically (for double-page backgrounds)
  const updatePageBackgroundsBatch = useCallback(
    async (pageBackgroundPairs) => {
      if (!bookId || !pageBackgroundPairs || pageBackgroundPairs.length === 0) {
        return
      }

      // Capture single snapshot BEFORE any changes (atomic operation)
      pushHistorySnapshot(UPDATE_PAGE_BACKGROUND)

      // Update all pages via API
      const updatePromises = pageBackgroundPairs.map(({ pageNumber, backgroundId }) => {
        const normalizedPageNumber = normalizePageId(pageNumber)
        const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)

        if (!selectedPage) {
          return Promise.resolve()
        }

        return updatePageLayoutAPI({
          bookId,
          templateId: selectedPage.templateId,
          page: normalizedPageNumber,
          elements: selectedPage.elements,
          stickers: selectedPage.stickers,
          customLayoutElements: selectedPage.customLayoutElements,
          backgroundId: backgroundId ?? null,
          onSuccess: () => {
            // Dispatch state update for this page
            dispatch({
              type: UPDATE_PAGE_BACKGROUND,
              payload: { pageNumber: normalizedPageNumber, backgroundId: backgroundId ?? null },
            })
          },
          onError: () => {
            showStatusOverlay({
              message: t(
                'MemoryBookProvider.problemUpdatingBackground.description',
                'There was a problem updating the background, please try again later.'
              ),
              type: 'error',
              dismissable: true,
            })
          },
        })
      })

      // Wait for all updates to complete
      await Promise.all(updatePromises)
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t, pushHistorySnapshot]
  )

  const updateMobileView = useCallback((isMobile) => {
    dispatch({ type: SET_MOBILE_MODE, payload: { isMobile } })
  }, [])

  const loadBook = useCallback(
    async (requestedBookId) => {
      if (!requestedBookId) {
        console.error('No bookId provided to loadBook')
        setBookLoadSettled(true)
        return
      }

      // Already loaded: nothing to fetch, but the loading gate still has to open
      if (loadedBookIdRef.current === requestedBookId) {
        setBookLoadSettled(true)
        return
      }

      // A load is already in flight; its finally block opens the gate
      if (loadingRef.current) {
        return
      }

      loadingRef.current = true

      try {
        const { bookData, error: fetchError } = await getBookById(requestedBookId)

        if (fetchError) {
          console.error('Error loading book:', fetchError)
          showStatusOverlay({
            message: t('MemoryBookProvider.errorLoadingBook', 'Failed to load book. Please try again.'),
            type: 'error',
            dismissable: true,
          })
          return
        }

        if (!bookData) {
          console.error('Book not found:', requestedBookId)
          showStatusOverlay({
            message: t('MemoryBookProvider.bookNotFound', 'Book not found. It may have been deleted.'),
            type: 'error',
            dismissable: true,
          })
          return
        }

        dispatch({ type: SET_BOOK, payload: bookData })
        loadedBookIdRef.current = requestedBookId
      } catch (err) {
        console.error('Exception in loadBook:', err)
        showStatusOverlay({
          message: t('MemoryBookProvider.errorLoadingBook', 'Failed to load book. Please try again.'),
          type: 'error',
          dismissable: true,
        })
      } finally {
        loadingRef.current = false
        setBookLoadSettled(true)
      }
    },
    [dispatch, showStatusOverlay, t]
  )

  const memoryViewerOverlay = useOverlay()
  const [memoryViewerMemoryId, setMemoryViewerMemoryId] = useState(null)

  const memoryLoader = useCallback(async () => {
    if (!memoryViewerMemoryId) return []
    const response = await getMemory(memoryViewerMemoryId)

    if (!response) {
      return []
    }

    return Array.isArray(response) ? response : [response]
  }, [memoryViewerMemoryId])

  const values = useMemo(() => {
    return {
      memoryBook,
      currentViewingPages: memoryBook.currentPages,
      addBookPages,
      deleteBookPage,
      turnBookPage,
      updatePageOrder,
      updateViewingPages,
      updatePageLayout,
      updatePageContent,
      updatePageStickers,
      updatePageCustomLayoutElements,
      updateCustomLayoutElementData,
      updatePageBackground,
      updatePageBackgroundsBatch,
      updateMobileView,
      loadBook,
      isError: error,
      isLoading: loading || (skipFirstBookFetch && !bookLoadSettled),
      memoryViewerOverlay,
      memoryViewerMemoryId,
      setMemoryViewerMemoryId,
      memoryLoader,
      siteConfig,
      // Undo/Redo controls (only available when feature flag is enabled)
      canUndo: isUndoRedoEnabled ? undoRedoControls.canUndo : false,
      canRedo: isUndoRedoEnabled ? undoRedoControls.canRedo : false,
      performUndo,
      performRedo,
      isSaving,
    }
  }, [
    memoryBook,
    addBookPages,
    deleteBookPage,
    turnBookPage,
    updatePageOrder,
    updateViewingPages,
    updatePageLayout,
    updatePageContent,
    updatePageStickers,
    updatePageCustomLayoutElements,
    updateCustomLayoutElementData,
    updatePageBackground,
    updatePageBackgroundsBatch,
    updateMobileView,
    loadBook,
    error,
    loading,
    skipFirstBookFetch,
    bookLoadSettled,
    memoryViewerOverlay,
    memoryViewerMemoryId,
    memoryLoader,
    siteConfig,
    isUndoRedoEnabled,
    undoRedoControls.canUndo,
    undoRedoControls.canRedo,
    performUndo,
    performRedo,
    isSaving,
  ])

  return <MemoryBookContext.Provider value={values}>{children}</MemoryBookContext.Provider>
}
